import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  MicIcon, VideoIcon, VideoOffIcon, MicOffIcon,
  MonitorIcon, MoreVerticalIcon, ArrowRightIcon,
  Loader2Icon, MessageSquareIcon, PhoneOffIcon
} from 'lucide-react';
import { useInterviewStore } from '../store';
import { useMediaRecording } from '../hooks/useMediaRecording';
import { useSpeechSynthesis } from '../hooks/useSpeechSynthesis';
import { InterviewTimer } from './InterviewTimer';
import { generateInterviewQuestion, analyzeResponse } from '../services/openai';
import { InterviewSummary } from './InterviewSummary';
import { uploadVideo } from '../services/supabase';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

const AVATAR_URL = "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?q=80&w=300&auto=format&fit=crop";

interface Message {
  role: 'ai' | 'user' | 'system';
  content: string;
  timestamp: Date;
  status?: 'sending' | 'sent' | 'error';
}

export function InterviewProcess() {
  const { t } = useTranslation();
  const [isRecording, setIsRecording] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isMicEnabled, setIsMicEnabled] = useState(true);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [previousQuestions, setPreviousQuestions] = useState<string[]>([]);
  const [previousAnswers, setPreviousAnswers] = useState<string[]>([]);
  const [isProcessingAnswer, setIsProcessingAnswer] = useState(false);
  const [isInterviewEnded, setIsInterviewEnded] = useState(false);
  const [currentAnswer, setCurrentAnswer] = useState('');
  const [showNextButton, setShowNextButton] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(true);
  const [showEndDialog, setShowEndDialog] = useState(false);
  const [isAvatarSpeaking, setIsAvatarSpeaking] = useState(false);
  const [currentVideoBlob, setCurrentVideoBlob] = useState<Blob | null>(null);
  const [isVoiceInitialized, setIsVoiceInitialized] = useState(false);
  
  const [questionNumber, setQuestionNumber] = useState(1);
  const [totalQuestions] = useState(5);
  
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const mainVideoRef = useRef<HTMLVideoElement>(null);
  const isAskingQuestionRef = useRef(false);
  
  const { 
    startRecording, 
    stopRecording,
    stream,
    audioTranscript,
    error: mediaError,
    videoBlob
  } = useMediaRecording((text) => {
    if (text) {
      setCurrentAnswer(text);
      setMessages(prev => [...prev, { 
        role: 'user', 
        content: text, 
        timestamp: new Date() 
      }]);
      setShowNextButton(true);
    }
  });
  
  const { speak, stop: stopSpeaking, isSpeaking } = useSpeechSynthesis();
  const jobDescription = useInterviewStore((state) => state.jobDescription);
  const interviewConfig = useInterviewStore((state) => state.interviewConfig);
  const currentInterview = useInterviewStore((state) => state.currentInterview);
  const updateInterview = useInterviewStore((state) => state.updateInterview);

  const RESPONSE_TIME_LIMIT = 30;

  useEffect(() => {
    const initVoice = async () => {
      try {
        await new Promise<void>((resolve) => {
          if (window.speechSynthesis.getVoices().length > 0) {
            resolve();
          } else {
            window.speechSynthesis.onvoiceschanged = () => resolve();
          }
        });
        setIsVoiceInitialized(true);
      } catch (error) {
        console.error('Voice initialization error:', error);
        toast.error(t('interview.error.voiceInitialization'));
      }
    };

    initVoice();
  }, [t]);

  useEffect(() => {
    if (!jobDescription || !isVoiceInitialized || !currentInterview?.currentQuestion || currentInterview.hasIntroduced) return;

    const startInterview = async () => {
      try {
        // Small delay to ensure everything is ready
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Set and speak the initial greeting
        const greeting = currentInterview.currentQuestion;
        setMessages([{
          role: 'ai',
          content: greeting,
          timestamp: new Date()
        }]);

        setIsAvatarSpeaking(true);
        await speak(greeting);
        setIsAvatarSpeaking(false);
        
        setCurrentQuestion(greeting);
        setPreviousQuestions([greeting]);

        // Mark that Sarah has introduced herself
        updateInterview(currentInterview.id, { hasIntroduced: true });
      } catch (error) {
        console.error('Error starting interview:', error);
        toast.error(t('interview.error.startingInterview'));
      }
    };

    startInterview();
  }, [jobDescription, isVoiceInitialized, currentInterview?.currentQuestion, currentInterview?.hasIntroduced, speak, t, updateInterview, currentInterview?.id]);

  useEffect(() => {
    if (videoBlob) {
      setCurrentVideoBlob(videoBlob);
    }
  }, [videoBlob]);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (stream && mainVideoRef.current) {
      mainVideoRef.current.srcObject = stream;
      mainVideoRef.current.play().catch(console.error);
    }
  }, [stream]);

  const getNextQuestion = useCallback(async () => {
    if (!jobDescription || isAskingQuestionRef.current) return null;
    
    try {
      isAskingQuestionRef.current = true;
      const question = await generateInterviewQuestion(
        jobDescription,
        interviewConfig,
        previousQuestions,
        previousAnswers
      );
      return question;
    } catch (error) {
      console.error('Error generating question:', error);
      toast.error(t('interview.error.questionGeneration'));
      return null;
    } finally {
      isAskingQuestionRef.current = false;
    }
  }, [jobDescription, interviewConfig, previousQuestions, previousAnswers, t]);

  const handleEndInterview = useCallback(() => {
    setIsInterviewEnded(true);
    if (currentInterview) {
      updateInterview(currentInterview.id, { status: 'completed' });
    }
  }, [currentInterview, updateInterview]);

  const handleStopRecording = useCallback(async () => {
    if (isProcessingAnswer) return;
    
    stopRecording();
    setIsTimerActive(false);
    setIsRecording(false);
    
    setMessages(prev => [
      ...prev,
      { role: 'system', content: t('interview.recording.stopped'), timestamp: new Date() }
    ]);
  }, [stopRecording, isProcessingAnswer, t]);

  const handleStartRecording = useCallback(async () => {
    if (isSpeaking()) {
      stopSpeaking();
    }
    if (isProcessingAnswer) return;
    
    setIsTimerActive(true);
    setIsRecording(true);
    setShowNextButton(false);
    await startRecording();
    
    setMessages(prev => [...prev, {
      role: 'system',
      content: t('interview.recording.started'),
      timestamp: new Date()
    }]);

    setTimeout(() => {
      if (isRecording) {
        handleStopRecording();
      }
    }, RESPONSE_TIME_LIMIT * 1000);
  }, [isSpeaking, stopSpeaking, startRecording, isProcessingAnswer, t, isRecording, handleStopRecording]);

  const handleTimeUp = useCallback(() => {
    if (isRecording) {
      handleStopRecording();
    }
  }, [isRecording, handleStopRecording]);

  const handleNextQuestion = useCallback(async () => {
    if (!jobDescription || !currentQuestion || !currentInterview || !currentAnswer) return;
    
    setShowNextButton(false);
    setIsProcessingAnswer(true);

    try {
      setMessages(prev => [...prev, {
        role: 'system',
        content: t('interview.feedback.analyzing'),
        timestamp: new Date(),
        status: 'sending'
      }]);

      const feedback = await analyzeResponse(currentQuestion, currentAnswer, jobDescription, interviewConfig);
      
      let videoUrl;
      if (currentVideoBlob) {
        try {
          videoUrl = await uploadVideo(
            currentInterview.id,
            crypto.randomUUID(),
            questionNumber,
            currentVideoBlob
          );
        } catch (error) {
          console.error('Error uploading video:', error);
          toast.error(t('interview.error.videoUpload'));
        }
      }
      
      updateInterview(currentInterview.id, {
        responses: [...(currentInterview.responses || []), { 
          question: currentQuestion, 
          answer: currentAnswer, 
          feedback,
          videoUrl
        }]
      });

      setPreviousAnswers(prev => [...prev, currentAnswer]);
      setCurrentAnswer('');
      setCurrentVideoBlob(null);
      
      if (feedback.response) {
        setIsAvatarSpeaking(true);
        await speak(feedback.response);
        setIsAvatarSpeaking(false);
      }

      const nextQuestionNumber = questionNumber + 1;
      setQuestionNumber(nextQuestionNumber);

      if (nextQuestionNumber > totalQuestions) {
        handleEndInterview();
        return;
      }

      const nextQuestion = await getNextQuestion();
      if (nextQuestion) {
        setCurrentQuestion(nextQuestion);
        setPreviousQuestions(prev => [...prev, nextQuestion]);
        setMessages(prev => [...prev, { 
          role: 'ai', 
          content: nextQuestion, 
          timestamp: new Date() 
        }]);
        setIsAvatarSpeaking(true);
        await speak(nextQuestion);
        setIsAvatarSpeaking(false);
      }
    } catch (error) {
      console.error('Error processing answer:', error);
      toast.error(t('interview.feedback.error'));
      setMessages(prev => [...prev, {
        role: 'system',
        content: t('interview.feedback.error'),
        timestamp: new Date(),
        status: 'error'
      }]);
      setIsAvatarSpeaking(false);
    } finally {
      setIsProcessingAnswer(false);
    }
  }, [
    currentQuestion,
    currentAnswer,
    currentVideoBlob,
    jobDescription,
    interviewConfig,
    getNextQuestion,
    speak,
    t,
    currentInterview,
    updateInterview,
    questionNumber,
    totalQuestions,
    handleEndInterview
  ]);

  if (isInterviewEnded && currentInterview) {
    return <InterviewSummary interview={currentInterview} />;
  }

  return (
    <div className="h-[calc(100vh-80px)] bg-gray-900">
      <div className="bg-gray-800 px-4 py-2">
        <div className="max-w-7xl mx-auto flex items-center justify-between text-white">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">
              Question {questionNumber} of {totalQuestions}
            </span>
            <div className="w-64 bg-gray-700 rounded-full h-2">
              <div
                className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(questionNumber / totalQuestions) * 100}%` }}
              />
            </div>
          </div>
          <button
            onClick={() => setShowEndDialog(true)}
            className="text-gray-300 hover:text-white flex items-center gap-2 px-3 py-1 rounded-md hover:bg-gray-700"
          >
            <PhoneOffIcon className="h-4 w-4" />
            <span>End Interview</span>
          </button>
        </div>
      </div>

      <div className="h-full p-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 h-full">
          <div className="relative bg-gray-800 rounded-lg overflow-hidden">
            <video
              ref={mainVideoRef}
              autoPlay
              playsInline
              muted={!isRecording}
              className={`w-full h-full object-cover ${!isVideoEnabled ? 'hidden' : ''}`}
            />
            
            {!isVideoEnabled && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-700">
                <VideoOffIcon className="h-24 w-24 text-gray-500" />
              </div>
            )}

            <div className="absolute bottom-4 right-4 w-48 h-36 rounded-lg overflow-hidden shadow-lg">
              <div className="relative w-full h-full bg-gray-800">
                <img
                  src={AVATAR_URL}
                  alt="AI Interviewer"
                  className="w-full h-full object-cover"
                />
                {isAvatarSpeaking && (
                  <div className="absolute bottom-2 left-2 flex items-center gap-2 bg-indigo-500 text-white px-2 py-1 rounded-full text-xs">
                    <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                    Speaking
                  </div>
                )}
              </div>
            </div>

            {isRecording && (
              <div className="absolute top-4 left-4 flex items-center gap-2 bg-red-500 text-white px-3 py-1.5 rounded-full text-sm">
                <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                REC
              </div>
            )}
            
            {mediaError && (
              <div className="absolute top-4 right-4 bg-red-500 text-white px-3 py-1.5 rounded-full text-sm">
                {mediaError}
              </div>
            )}

            <div className="absolute bottom-0 left-0 right-0 p-4">
              <InterviewTimer
                duration={30}
                onTimeUp={handleTimeUp}
                isActive={isTimerActive}
              />
            </div>
          </div>

          <div className={`flex flex-col bg-gray-800 rounded-lg overflow-hidden transition-all duration-300 ${isChatOpen ? 'opacity-100' : 'opacity-0'}`}>
            <div className="flex-1 overflow-y-auto p-4 space-y-4" ref={chatContainerRef}>
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`flex ${
                    message.role === 'ai' 
                      ? 'justify-start' 
                      : message.role === 'user' 
                      ? 'justify-end' 
                      : 'justify-center'
                  }`}
                >
                  <div
                    className={`max-w-[80%] rounded-lg p-3 ${
                      message.role === 'ai'
                        ? 'bg-indigo-600 text-white'
                        : message.role === 'user'
                        ? 'bg-gray-700 text-white'
                        : 'bg-gray-600 text-gray-200 text-sm'
                    }`}
                  >
                    <p>{message.content}</p>
                    <span className="text-xs opacity-70 mt-1 block">
                      {message.timestamp.toLocaleTimeString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <div className="p-4 bg-gray-700 space-y-4">
              <div className="flex justify-center gap-4">
                {!isRecording && !isProcessingAnswer && !isInterviewEnded && (
                  <button
                    onClick={handleStartRecording}
                    disabled={isSpeaking()}
                    className="px-6 py-2 bg-green-500 text-white rounded-full hover:bg-green-600 disabled:opacity-50 flex items-center gap-2"
                  >
                    <MicIcon className="h-5 w-5" />
                    {t('interview.controls.startRecording')}
                  </button>
                )}
                
                {isRecording && (
                  <button
                    onClick={handleStopRecording}
                    className="px-6 py-2 bg-red-500 text-white rounded-full hover:bg-red-600 flex items-center gap-2"
                  >
                    <MicOffIcon className="h-5 w-5" />
                    {t('interview.controls.stopRecording')}
                  </button>
                )}

                {showNextButton && !isProcessingAnswer && (
                  <button
                    onClick={handleNextQuestion}
                    className="px-6 py-2 bg-indigo-500 text-white rounded-full hover:bg-indigo-600 flex items-center gap-2"
                  >
                    <ArrowRightIcon className="h-5 w-5" />
                    {t('interview.controls.continue')}
                  </button>
                )}

                {isProcessingAnswer && (
                  <div className="flex items-center gap-2 text-gray-300">
                    <Loader2Icon className="h-5 w-5 animate-spin" />
                    {t('interview.feedback.analyzing')}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="h-20 bg-gray-800 border-t border-gray-700">
        <div className="max-w-7xl mx-auto h-full px-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setIsMicEnabled(!isMicEnabled)}
              className={`p-3 rounded-full ${
                isMicEnabled ? 'bg-gray-700 hover:bg-gray-600' : 'bg-red-500 hover:bg-red-600'
              } text-white transition-colors`}
            >
              {isMicEnabled ? <MicIcon className="h-6 w-6" /> : <MicOffIcon className="h-6 w-6" />}
            </button>
            <button
              onClick={() => setIsVideoEnabled(!isVideoEnabled)}
              className={`p-3 rounded-full ${
                isVideoEnabled ? 'bg-gray-700 hover:bg-gray-600' : 'bg-red-500 hover:bg-red-600'
              } text-white transition-colors`}
            >
              {isVideoEnabled ? <VideoIcon className="h-6 w-6" /> : <VideoOffIcon className="h-6 w-6" />}
            </button>
          </div>

          <div className="flex items-center space-x-4">
            <button
              onClick={() => setIsChatOpen(!isChatOpen)}
              className={`p-3 rounded-full ${
                isChatOpen ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-gray-700 hover:bg-gray-600'
              } text-white transition-colors`}
            >
              <MessageSquareIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="flex items-center space-x-4">
            <button className="p-3 rounded-full bg-gray-700 hover:bg-gray-600 text-white transition-colors">
              <MonitorIcon className="h-6 w-6" />
            </button>
            <button className="p-3 rounded-full bg-gray-700 hover:bg-gray-600 text-white transition-colors">
              <MoreVerticalIcon className="h-6 w-6" />
            </button>
          </div>
        </div>
      </div>

      {showEndDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">
              {t('interview.endDialog.title')}
            </h3>
            <p className="text-gray-600 mb-6">
              {t('interview.endDialog.message')}
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setShowEndDialog(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                {t('interview.endDialog.cancel')}
              </button>
              <button
                onClick={handleEndInterview}
                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
              >
                {t('interview.endDialog.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}